/**
 * Decisions API service
 * Handles HTTP requests to the decisions endpoints
 * Used for submitting student answers and creating new decisions
 */

import { api } from '@/lib/api';
import type { CreateDecisionRequest, CreateDecisionResponse } from '@/types/decision';

/**
 * Create a new decision (message) in a tutoring session
 * Submits student answer or creates new tutor question/feedback
 *
 * @param request - Decision creation request with userId, sessionId, role, decisionType, content
 * @returns Promise resolving to CreateDecisionResponse with decisionId, sessionId, timestamp
 * @throws {DecisionError} If validation fails, session not found, or server error
 *
 * @example
 * ```typescript
 * const response = await createDecision({
 *   userId: 'user_abc123',
 *   sessionId: 'sess_xyz789',
 *   role: 'student',
 *   decisionType: 'answer',
 *   content: 'Because she wanted to see the sunset'
 * });
 * // Returns: { decisionId: 'dec_123', sessionId: 'sess_xyz789', timestamp: '2026-01-23T10:00:00.000Z' }
 * ```
 */
export async function createDecision(
  request: CreateDecisionRequest
): Promise<CreateDecisionResponse> {
  const response = await api.post<CreateDecisionResponse>('/decisions', request);
  return response.data;
}
